import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertCircle,
  ArrowLeft,
  Eye,
  EyeOff,
  GripVertical,
  Inbox,
  Plus,
  RefreshCw,
  Settings,
  Sparkles,
  Trash2,
  Youtube,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import {
  useAddYtVideo,
  useBulkVideoAction,
  useDeleteYtVideo,
  usePollYtPlaylist,
  useRefreshYtMetadata,
  useReorderYtVideos,
  useUpdateYtVideo,
  ytErrorMessage,
  ytPlaylistDetailQueryOptions,
  ytPlaylistVideosQueryOptions,
} from '@/lib/youtube-admin';
import { PlaylistSettingsDialog } from '@/components/videos/playlist-settings-dialog';
import type { YoutubeVideoRecord } from '@news-cms/shared';

export const Route = createFileRoute('/_authenticated/videos/$playlistId')({
  component: PlaylistDetailPage,
});

const PAGE_SIZE = 50;

function PlaylistDetailPage() {
  const { playlistId } = Route.useParams();
  const { user } = useSession();
  const canManage = hasPermission(user, 'feed.manage');
  const [offset, setOffset] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Selected video ids for bulk actions. Cleared on page change or after
  // any bulk mutation succeeds.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const playlistQuery = useQuery(ytPlaylistDetailQueryOptions(playlistId));
  const videosQuery = useQuery(
    ytPlaylistVideosQueryOptions(playlistId, { limit: PAGE_SIZE, offset }),
  );
  const poll = usePollYtPlaylist(playlistId);
  const refreshMeta = useRefreshYtMetadata(playlistId);
  const bulkAction = useBulkVideoAction(playlistId);
  const reorder = useReorderYtVideos(playlistId);

  // Drag sensor — require 5px movement before drag starts so click on
  // an action button inside the card doesn't accidentally trigger a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี feed.manage
      </div>
    );
  }

  const playlist = playlistQuery.data;
  const videos = videosQuery.data?.items ?? [];
  const total = videosQuery.data?.total ?? 0;

  // Selection helpers — use Set for O(1) lookup, immutable updates so
  // React re-renders. Cap "select all" to the currently-rendered page so
  // bulk actions can't act on rows the admin hasn't seen.
  const allSelectedOnPage = videos.length > 0 && videos.every((v) => selectedIds.has(v.id));
  const someSelectedOnPage = videos.some((v) => selectedIds.has(v.id));

  const toggleOne = (videoId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        for (const v of videos) next.delete(v.id);
      } else {
        for (const v of videos) next.add(v.id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulk = (action: 'hide' | 'show' | 'delete') => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (action === 'delete' && !window.confirm(`ลบวิดีโอ ${ids.length} ตัว?`)) return;
    bulkAction.mutate(
      { ids, action },
      {
        onSuccess: () => clearSelection(),
      },
    );
  };

  const handleRefreshMeta = () => {
    if (videos.length === 0) return;
    refreshMeta.mutate();
  };

  // Local ordered ids for the sortable list — synced from server data
  // but allowed to drift during a drag so the optimistic reorder feels
  // instant. After drop we PATCH and TanStack refetches.
  const orderedIds = useMemo(() => videos.map((v) => v.id), [videos]);

  // Drag-reorder is only enabled when the entire playlist fits on one
  // page. Otherwise dragging would re-assign positions starting at 0
  // and break the global ordering across pages. Admins with longer
  // playlists must paginate to a single page or edit position numerically.
  // System playlists rebuild positions on every refresh — disabling
  // drag (and hiding bulk + per-row actions below) keeps the UI honest
  // about what the operator can persist. Real playlists still need
  // single-page-only drag to keep positions globally consistent.
  const dragEnabled = !playlist?.isSystem && total <= PAGE_SIZE;
  const isSystemPlaylist = playlist?.isSystem ?? false;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = orderedIds.indexOf(String(active.id));
    const to = orderedIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const next = arrayMove(orderedIds, from, to);
    reorder.mutate(next);
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <Link
          to="/videos"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeft className="h-4 w-4" />
          กลับไปทุก playlist
        </Link>

        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <Youtube className="text-brand-600 h-6 w-6" />
              {playlist?.name ?? 'กำลังโหลด...'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {total} วิดีโอ {playlist && `· slug: ${playlist.slug}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              <Settings className="mr-1.5 h-3.5 w-3.5" /> ตั้งค่า
            </Button>
            {/* Hide refresh-metadata + manual-add for system playlists:
                backend returns 409 SYSTEM_PLAYLIST. "ดึงใหม่" stays —
                that triggers runPopularRefresh for the system row. */}
            {!playlist?.isSystem && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshMeta}
                disabled={refreshMeta.isPending || videos.length === 0}
                title="ดึง duration + ข้อมูล channel จาก YouTube API"
              >
                <Sparkles
                  className={`mr-1.5 h-3.5 w-3.5 ${refreshMeta.isPending ? 'animate-pulse' : ''}`}
                />
                {refreshMeta.isPending ? 'กำลังดึง...' : 'ดึง metadata'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => poll.mutate()}
              disabled={poll.isPending}
              title={
                playlist?.isSystem
                  ? 'Refresh ทันที (top 20 by viewCount จาก channel)'
                  : 'Poll YT API'
              }
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${poll.isPending ? 'animate-spin' : ''}`} />
              ดึงใหม่
            </Button>
            {!playlist?.isSystem && (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> เพิ่มวิดีโอ
              </Button>
            )}
          </div>
        </div>

        {playlist?.isSystem && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            🌟 <strong>Playlist ระบบ — รายการมาจากอัลกอริทึม</strong> (top 20 by viewCount, window
            ขยาย 3→7→14→30 วันอัตโนมัติ). เพิ่ม/ลบ/ซ่อน/เรียงด้วยมือไม่ได้: ทุก refresh
            จะเขียนทับใหม่ทั้งหมด. ไม่ออกที่ public {`/playlists`} (admin-only).
          </div>
        )}

        {refreshMeta.data && (
          <Alert>
            <Sparkles className="h-4 w-4" />
            <AlertDescription>
              อัปเดต metadata แล้ว {refreshMeta.data.refreshed} ตัว
              {refreshMeta.data.missing > 0 &&
                ` (${refreshMeta.data.missing} ตัวหาไม่เจอบน YouTube — อาจถูกลบ)`}
            </AlertDescription>
          </Alert>
        )}
        {refreshMeta.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{ytErrorMessage(refreshMeta.error)}</AlertDescription>
          </Alert>
        )}

        {playlist?.lastError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{playlist.lastError}</AlertDescription>
          </Alert>
        )}

        {(playlistQuery.isError || videosQuery.isError) && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {ytErrorMessage(playlistQuery.error ?? videosQuery.error)}
            </AlertDescription>
          </Alert>
        )}

        {videosQuery.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <Inbox className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ยังไม่มีวิดีโอใน playlist</p>
            <p className="text-xs text-slate-400">
              กด "ดึงใหม่" เพื่อ poll หรือ "เพิ่มวิดีโอ" เพื่อเพิ่มเอง
            </p>
          </div>
        ) : (
          <>
            {/* Selection + bulk action toolbar — always rendered so the
                user sees "Select all" + drag-disabled hint even with no
                selection yet. Hidden entirely on system playlists since
                bulk actions all 409 SYSTEM_PLAYLIST. */}
            {!isSystemPlaylist && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-900">
                <label className="flex items-center gap-2 px-2 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={allSelectedOnPage}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelectedOnPage && !allSelectedOnPage;
                    }}
                    onChange={toggleAllOnPage}
                  />
                  เลือกทั้งหน้า ({videos.length} ตัว)
                  {selectedIds.size > 0 && (
                    <span className="ml-2 text-slate-400">เลือกแล้ว {selectedIds.size}</span>
                  )}
                </label>
                <div className="flex flex-wrap items-center gap-1">
                  {!dragEnabled && (
                    <span className="px-2 text-[11px] text-slate-400">
                      ลากเรียงได้เฉพาะเมื่ออยู่หน้าเดียว
                    </span>
                  )}
                  {selectedIds.size > 0 ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulk('hide')}
                        disabled={bulkAction.isPending}
                      >
                        <EyeOff className="mr-1 h-3 w-3" /> ซ่อน
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulk('show')}
                        disabled={bulkAction.isPending}
                      >
                        <Eye className="mr-1 h-3 w-3" /> แสดง
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulk('delete')}
                        disabled={bulkAction.isPending}
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="mr-1 h-3 w-3" /> ลบ
                      </Button>
                      <Button size="sm" variant="outline" onClick={clearSelection}>
                        ยกเลิก
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={dragEnabled ? handleDragEnd : undefined}
            >
              <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {videos.map((v) => (
                    <VideoCard
                      key={v.id}
                      video={v}
                      playlistId={playlistId}
                      selected={selectedIds.has(v.id)}
                      onToggleSelect={() => toggleOne(v.id)}
                      dragEnabled={dragEnabled}
                      readOnly={isSystemPlaylist}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>
                  แสดง {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} จาก {total}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={offset === 0}
                    onClick={() => {
                      setOffset(Math.max(0, offset - PAGE_SIZE));
                      clearSelection();
                    }}
                  >
                    ก่อนหน้า
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => {
                      setOffset(offset + PAGE_SIZE);
                      clearSelection();
                    }}
                  >
                    ถัดไป
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AddVideoDialog playlistId={playlistId} open={addOpen} onOpenChange={setAddOpen} />
      {playlist && (
        <PlaylistSettingsDialog
          playlist={playlist}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      )}
    </div>
  );
}

function VideoCard({
  video,
  playlistId,
  selected,
  onToggleSelect,
  dragEnabled,
  readOnly,
}: {
  video: YoutubeVideoRecord;
  playlistId: string;
  selected: boolean;
  onToggleSelect: () => void;
  dragEnabled: boolean;
  /** System-playlist rows — hide checkbox + edit/hide/delete buttons
   *  since the backend rejects per-video mutations on system rows. */
  readOnly: boolean;
}) {
  const update = useUpdateYtVideo(playlistId);
  const del = useDeleteYtVideo(playlistId);
  const [editOpen, setEditOpen] = useState(false);
  // dnd-kit hook — refs + transform are wired to the <li>, but the
  // listeners go ONLY on the drag handle so clicks on action buttons
  // don't initiate a drag.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: video.id,
    disabled: !dragEnabled,
  });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleToggleHidden = () => {
    update.mutate({ videoId: video.id, input: { isHidden: !video.isHidden } });
  };

  const handleDelete = () => {
    if (!window.confirm(`ลบวิดีโอ "${video.title}"?`)) return;
    del.mutate(video.id);
  };

  const duration = video.durationSeconds !== null ? formatDuration(video.durationSeconds) : '';

  return (
    <li
      ref={setNodeRef}
      style={sortableStyle}
      className={`flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900 ${
        video.isHidden ? 'opacity-60' : ''
      } ${selected ? 'ring-brand-500 ring-2' : ''} ${isDragging ? 'z-10 shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-2">
        {!readOnly && (
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={selected}
            onChange={onToggleSelect}
            aria-label="เลือกวิดีโอนี้"
          />
        )}
        {dragEnabled && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="mt-0.5 cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:hover:text-slate-200"
            aria-label="ลากเรียง"
            title="ลากเพื่อเรียงลำดับ"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <a
          href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="relative block aspect-video flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800"
        >
          <img
            src={video.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
          {duration && (
            <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {duration}
            </span>
          )}
        </a>
      </div>
      <div className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-slate-50">
        {video.title}
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span>#{video.position}</span>
        {!readOnly && (
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              แก้ไข
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleToggleHidden}
              disabled={update.isPending}
              title={video.isHidden ? 'แสดง' : 'ซ่อน'}
            >
              {video.isHidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDelete} disabled={del.isPending}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      <EditVideoDialog
        video={video}
        playlistId={playlistId}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </li>
  );
}

/* ---------- Dialogs ---------- */

function AddVideoDialog({
  playlistId,
  open,
  onOpenChange,
}: {
  playlistId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const add = useAddYtVideo(playlistId);
  const [videoInput, setVideoInput] = useState('');
  const [submitError, setSubmitError] = useState<unknown>(null);

  const handleSave = () => {
    setSubmitError(null);
    add.mutate(
      { videoInput: videoInput.trim() },
      {
        onSuccess: () => {
          setVideoInput('');
          onOpenChange(false);
        },
        onError: (e) => setSubmitError(e),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>เพิ่มวิดีโอ</DialogTitle>
          <DialogDescription>
            ใส่ลิงก์ YouTube video หรือ video ID — ระบบจะดึง title / thumbnail / duration จาก
            YouTube ให้
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="yt-video-input">URL หรือ Video ID</Label>
          <Input
            id="yt-video-input"
            value={videoInput}
            onChange={(e) => setVideoInput(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        {submitError !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{ytErrorMessage(submitError)}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={add.isPending}>
            ยกเลิก
          </Button>
          <Button onClick={handleSave} disabled={add.isPending || !videoInput.trim()}>
            {add.isPending ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังเพิ่ม
              </>
            ) : (
              'เพิ่ม'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditVideoDialog({
  video,
  playlistId,
  open,
  onOpenChange,
}: {
  video: YoutubeVideoRecord;
  playlistId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const update = useUpdateYtVideo(playlistId);
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState(video.thumbnailUrl);
  const [position, setPosition] = useState(String(video.position));
  const [submitError, setSubmitError] = useState<unknown>(null);

  const handleSave = () => {
    setSubmitError(null);
    update.mutate(
      {
        videoId: video.id,
        input: {
          title: title.trim(),
          description: description.trim() || null,
          thumbnailUrl: thumbnailUrl.trim(),
          position: Number(position) || 0,
        },
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (e) => setSubmitError(e),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>แก้ไขวิดีโอ</DialogTitle>
          <DialogDescription>
            YouTube ID: <code>{video.youtubeId}</code>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-title">ชื่อ</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-desc">คำอธิบาย</Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-thumb">Thumbnail URL</Label>
            <Input
              id="edit-thumb"
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-pos">ตำแหน่ง (เรียงน้อย→มาก)</Label>
            <Input
              id="edit-pos"
              type="number"
              min={0}
              max={9999}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </div>

          {submitError !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{ytErrorMessage(submitError)}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            ยกเลิก
          </Button>
          <Button onClick={handleSave} disabled={update.isPending || !title.trim()}>
            {update.isPending ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
              </>
            ) : (
              'บันทึก'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Helpers ---------- */

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
